// =============================================================================
// @tnb/lot-cases — Single source of truth for the CASE COUNT of raw material taken from
// `ics.hte_lot` (WCO-2746).
//
// WHY THIS EXISTS — the source of the number, not the rounding:
// A lot knows its own case weight: `actual_weight / actual_quantity`. `ics.inventory.unit_weight`
// is a CATALOG field on the ITEM and is NOT the weight of the cases physically in the building.
// On dev these disagree materially (catalog 32.00 vs real 35.00 lb/case on `G WG06 ara` — 9%).
// Deriving cases as `weight / catalog unit_weight` therefore INVENTS a count, and invents phantom
// leftovers with it (132.00 lbs of three real 44.00-lb cases rendering as "2 cs + 43.82 lbs").
//
// THE RULE:
//   - material from a lot        → the LOT's own ratio (this package)
//   - RM-NI (`is_ni_remnant`)    → NO cases. Not-intact is by definition less than a full case;
//                                  it contributes WEIGHT only (shown as the "+ N lbs" tail)
//   - inbound warehouse transfer → `ics.inventory.unit_weight` via `@tnb/transfer-cases`, because
//                                  no lot exists yet and there is nothing physical to count
//
// Cases OPENED rounds UP (you cannot draw 100 lbs from two 44-lb cases), capped by what the lot
// holds. Cases ON HAND rounds DOWN (a part-case is not a case) — that is `lotWholeCases`.
// =============================================================================

'use strict';

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isNiRemnant(lot) {
  const v = lot && (lot.is_ni_remnant !== undefined ? lot.is_ni_remnant : lot.isNiRemnant);
  return v === true || v === 't' || v === 'Y' || v === 'true';
}

/**
 * The weight of ONE case of this lot, from the lot's own received cases:weight ratio.
 * @returns {number|null} lb per case, or null when the lot has no case basis
 *          (RM-NI, or a weight-only/bulk lot with no received case count).
 */
function lotCaseWeight(lot) {
  if (!lot || isNiRemnant(lot)) return null;
  const cases = num(lot.actual_quantity);
  const weight = num(lot.actual_weight);
  if (!(cases > 0) || !(weight > 0)) return null;
  return weight / cases;
}

/**
 * Whole cases still ON HAND in this lot (FLOOR — a part-case is not a case).
 * RM-NI and weight-only lots have none.
 */
function lotWholeCases(lot) {
  const caseWeight = lotCaseWeight(lot);
  if (!caseWeight) return 0;
  const live = num(lot.current_amount);
  if (!(live > 0)) return 0;
  return Math.floor(live / caseWeight);
}

/**
 * Weight in this lot that is NOT backed by a whole case — the loose tail: all of an RM-NI lot,
 * plus the part-case remainder of a partially drawn intact lot. Rendered as "+ N lbs".
 */
function lotLooseWeight(lot) {
  const live = num(lot.current_amount);
  if (!(live > 0)) return 0;
  const caseWeight = lotCaseWeight(lot);
  if (!caseWeight) return live;
  return Math.max(0, live - lotWholeCases(lot) * caseWeight);
}

/**
 * Cases that must be OPENED to draw `weight` at `caseWeight` lb/case — CEIL, because a case is
 * opened whole. 0 when there is no case basis (the draw is loose weight, not cases).
 */
function casesToOpen(weight, caseWeight) {
  const w = num(weight);
  const c = num(caseWeight);
  if (!(w > 0) || !(c > 0)) return 0;
  return Math.ceil(w / c);
}

/**
 * Roll a set of lots up into the availability an item's aggregate row advertises.
 * @returns {{ weight, cases, caseBackedWeight, looseWeight, caseWeight }}
 *          `caseWeight` is the pooled lb/case (case-backed weight ÷ cases), null when no cases.
 */
function aggregateLotAvailability(lots) {
  const rows = Array.isArray(lots) ? lots : [];
  let weight = 0;
  let cases = 0;
  let caseBackedWeight = 0;
  for (const lot of rows) {
    weight += num(lot.current_amount);
    const wholeCases = lotWholeCases(lot);
    cases += wholeCases;
    const cw = lotCaseWeight(lot);
    if (cw) caseBackedWeight += wholeCases * cw;
  }
  return {
    weight,
    cases,
    caseBackedWeight,
    looseWeight: Math.max(0, weight - caseBackedWeight),
    caseWeight: cases > 0 ? caseBackedWeight / cases : null,
  };
}

/**
 * How a draw of `weight` from an item's HTE availability breaks down for display and allocation.
 *
 * Draw order mirrors `LOT_CONSUME_PRIORITY_SQL` (@tnb/lot-status): the loose/RM-NI tail is used
 * before whole cases are opened. That is what makes the tooltip read "2 cs + 20 lbs" — 20 lbs of
 * RM-NI plus two intact cases — instead of pretending the RM-NI is part of a case.
 *
 * A case leaves the cooler whole, so `weight` (what is taken) is >= `weight` asked for. RM-NI is
 * drawn by weight — it is already less than a case.
 *
 * @param {number} weight             weight the session needs from HTE
 * @param {object} availability       from `aggregateLotAvailability` (or the API's aggregate row)
 * @returns {{ cases: number, looseWeight: number, weight: number, uncoveredWeight: number }}
 *          `weight` = allocated total (loose + cases × caseWeight): what to store and deduct.
 *          `uncoveredWeight` is what HTE cannot cover — the caller sizes that as a transfer using
 *          the catalog unit weight (`@tnb/transfer-cases`), since no lot exists for it yet.
 */
function splitHteDraw(weight, availability) {
  const want = num(weight);
  const avail = availability || {};
  const looseAvailable = num(avail.looseWeight);
  const casesAvailable = num(avail.cases);
  const caseWeight = num(avail.caseWeight);

  if (!(want > 0)) return { cases: 0, looseWeight: 0, weight: 0, uncoveredWeight: 0 };

  const looseWeight = Math.min(want, looseAvailable);
  const fromCases = want - looseWeight;
  if (!(fromCases > 0)) return { cases: 0, looseWeight, weight: looseWeight, uncoveredWeight: 0 };

  const wanted = casesToOpen(fromCases, caseWeight);
  const cases = Math.min(wanted, casesAvailable);
  const covered = cases * caseWeight;
  return {
    cases,
    looseWeight,
    weight: looseWeight + covered,
    uncoveredWeight: Math.max(0, fromCases - covered),
  };
}

// --- SQL ---------------------------------------------------------------------------------------
// Per-lot fragments for aggregate availability queries. `alias` is the ics.hte_lot alias.
// Kept byte-identical in shape to the JS above so an aggregate row and a client-side roll-up of
// the same lots can never disagree.

/** lb per case for a lot — NULL for RM-NI / weight-only lots. */
function LOT_CASE_WEIGHT_SQL(alias) {
  const l = alias || 'l';
  return `CASE WHEN COALESCE(${l}.is_ni_remnant, false) = false
                 AND COALESCE(${l}.actual_quantity, 0) > 0
                 AND COALESCE(${l}.actual_weight, 0) > 0
            THEN ${l}.actual_weight / ${l}.actual_quantity END`;
}

/** Whole cases on hand in a lot (FLOOR of live weight ÷ the lot's own case weight). */
function LOT_WHOLE_CASES_SQL(alias) {
  const l = alias || 'l';
  return `COALESCE(FLOOR(COALESCE(${l}.current_amount, 0) / NULLIF(${LOT_CASE_WEIGHT_SQL(l)}, 0)), 0)`;
}

/** Weight backed by whole cases (live weight minus the loose tail). */
function LOT_CASE_BACKED_WEIGHT_SQL(alias) {
  const l = alias || 'l';
  return `COALESCE(${LOT_WHOLE_CASES_SQL(l)} * NULLIF(${LOT_CASE_WEIGHT_SQL(l)}, 0), 0)`;
}

module.exports = {
  lotCaseWeight,
  lotWholeCases,
  lotLooseWeight,
  casesToOpen,
  aggregateLotAvailability,
  splitHteDraw,
  LOT_CASE_WEIGHT_SQL,
  LOT_WHOLE_CASES_SQL,
  LOT_CASE_BACKED_WEIGHT_SQL,
};
